import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, Minus, PencilLine, ScanFace, Sparkles, UserRoundX } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatFuzzyDate,
  formatLifespanYears,
  type MediaSummary,
  type PersonRef,
} from '@familienbaum/shared';
import {
  useCanWrite,
  useCreateRegion,
  useDeleteRegion,
  useDetectRegions,
  useEmbedRegions,
  useFaceSuggestions,
  useFaceVerdict,
  useMediaItem,
  useUpdateRegion,
} from '@/lib/queries';
import { ApiError } from '@/lib/api';
import { detectFaces } from '@/lib/faces/detect';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { Mono, PhotoFrame } from '@/components/ui/paper';
import { RegionCrop } from '@/components/media/face-crop';
import { RegionEditor } from '@/components/media/region-editor';
import { PersonPicker } from '@/components/person-picker';
import { cn } from '@/lib/utils';

/** Gold auf der dunklen Tafel – dieselben Knopfstile wie im Zuschnitt. */
const GOLD_ACTION =
  'bg-accent font-semibold text-accent-ink hover:bg-accent hover:brightness-105 active:brightness-95';
const GHOST_ON_INK = 'text-panel-ink hover:bg-panel-line hover:text-panel-ink';
const TOOL_ON_INK = 'bg-panel-line text-panel-ink hover:bg-panel-line hover:brightness-125';

/**
 * Was nach dem Hochladen kommt: Gesichter finden und benennen, sofort.
 *
 * Der Grund für diesen Schritt ist keine Bequemlichkeit, sondern ein Zeitfenster.
 * Wer gerade ein Familienfoto abfotografiert hat, sitzt in diesem Moment
 * meistens neben der Person, die weiß, wer darauf ist. Zehn Bilder später weiß
 * es niemand mehr, und das Bild landet im Stapel „unmarkiert", aus dem es
 * selten wieder herauskommt.
 *
 * Deshalb läuft die Erkennung von selbst an, ohne Knopf: sie kostet den
 * Benutzer nichts (gerechnet wird im Browser, das Bild liegt schon da) und
 * nimmt ihm den langweiligen Teil der Arbeit ab – das Rechteckeziehen. Der
 * interessante Teil, der Name, bleibt bei ihm.
 *
 * Zwei Ansichten:
 *
 *   - **Benennen** (Entwurf 2e, Schritt 3): das Bild mit den gefundenen
 *     Rahmen, darunter je ein offenes Gesicht. Mit Wiedererkennung steht ein
 *     Vorschlag da, sonst das Suchfeld.
 *   - **Von Hand markieren** (Schritt 4): der volle Markierungs-Editor für
 *     Gesichter, die die Erkennung nicht gefunden hat – abgewandt, verdeckt,
 *     in der Menge.
 */
export function ScanFaces({
  media,
  image,
  onClose,
  onNext,
  nextLabel = 'Nächstes Foto',
  position,
}: {
  /** Das gerade hochgeladene Bild. */
  media: MediaSummary;
  /**
   * Dieselbe Aufnahme als geladenes Bild im Browser.
   *
   * Gerechnet und zugeschnitten wird darauf und nicht auf den Ableitungen vom
   * Server: die entstehen erst im Hintergrund, und bis dahin gäbe es hier
   * nichts zu sehen. Die Markierungen sind auf 0…1 normiert und passen
   * deshalb auf beide.
   */
  image: HTMLImageElement;
  onClose: () => void;
  /**
   * Weiter zum nächsten Bild.
   *
   * Zwei Bedeutungen, je nachdem woher man kommt: nach dem Abfotografieren
   * öffnet das wieder die Kamera (und muss deshalb aus einem Klick heraus
   * laufen – iOS lässt es sonst nicht zu), beim Stapel aus der Galerie geht es
   * zum nächsten hochgeladenen Bild.
   */
  onNext?: () => void;
  nextLabel?: string;
  /** „Bild 2 von 5" – nur beim Durchgehen eines Stapels. */
  position?: { index: number; total: number };
}) {
  const canWrite = useCanWrite();
  const detail = useMediaItem(media.id);
  const detectRegions = useDetectRegions();
  const embed = useEmbedRegions();
  const updateRegion = useUpdateRegion();
  const createRegion = useCreateRegion();
  const deleteRegion = useDeleteRegion();
  const verdict = useFaceVerdict();
  const suggestions = useFaceSuggestions(media.id, canWrite);

  const [step, setStep] = useState<'naming' | 'marking'>('naming');
  const [detecting, setDetecting] = useState(true);
  /** „Andere Person": das Suchfeld übernimmt, der Vorschlag tritt zurück. */
  const [picking, setPicking] = useState(false);
  const [person, setPerson] = useState<PersonRef | null>(null);

  /**
   * Genau einmal erkennen – und zwar wirklich.
   *
   * Der Riegel ist eine Referenz und *kein* Abbruch im Aufräumen. Beides
   * zusammen hebt sich im Entwicklungsbetrieb gegenseitig auf: React ruft dort
   * jeden Effekt zweimal auf (`StrictMode`). Der erste Lauf setzt den Riegel,
   * das Aufräumen bricht ihn ab, der zweite Lauf sieht den Riegel und tut
   * nichts – die Erkennung stand dann für immer bei „wird gesucht".
   *
   * Ohne Abbruch läuft der erste Durchgang zu Ende und setzt den Zustand. Auf
   * einem ausgehängten Bauteil ist das seit React 18 folgenlos, und ein
   * doppeltes Erkennen (und damit doppelte Rahmen im Archiv) ist ausgeschlossen.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const faces = await detectFaces(image);
        if (faces.length === 0) {
          setDetecting(false);
          return;
        }

        // Nur die Geometrie und die Sicherheit des Modells gehen zum Server –
        // gerechnet wurde im Browser, das Bild hat das Gerät nicht verlassen.
        const created = await detectRegions.mutateAsync({
          mediaId: media.id,
          input: {
            regions: faces.map((face) => ({
              x: face.x,
              y: face.y,
              width: face.width,
              height: face.height,
              confidence: face.confidence,
            })),
          },
        });
        setDetecting(false);

        /**
         * Vermessen heißt: Vorschläge sind möglich. Das ist die Wiedererkennung
         * und setzt Schreibrechte voraus – Leser bleiben bei „Rahmen gefunden,
         * Namen bitte selbst".
         */
        if (canWrite) {
          try {
            await embed.mutateAsync({ image, regions: created });
          } catch {
            /* Ohne Merkmalsreihe gibt es keine Vorschläge – kein Grund für
               eine Fehlermeldung, das Benennen von Hand geht weiter. */
          }
        }
      } catch {
        setDetecting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.id]);

  const regions = detail.data?.regions ?? [];
  const open = regions.filter((region) => !region.person);
  const current = open[0] ?? null;
  const named = regions.length - open.length;

  /** Der Vorschlag zu genau diesem Rahmen, falls es einen gibt. */
  const cluster = current
    ? (suggestions.data?.clusters.find((entry) =>
        entry.faces.some((face) => face.regionId === current.id),
      ) ?? null)
    : null;
  const suggestion = picking ? null : (cluster?.suggestion ?? null);

  const assign = async (regionId: string, target: PersonRef) => {
    try {
      if (suggestion && suggestion.person.id === target.id && canWrite) {
        // Über das Urteil, nicht über den Rahmen: so lernt der Vorrat an
        // Vergleichsgesichtern mit, und die Ähnlichkeit wird mitgeschrieben.
        await verdict.mutateAsync({
          regionIds: [regionId],
          answer: 'yes',
          personId: target.id,
          similarity: suggestion.similarity,
        });
      } else {
        await updateRegion.mutateAsync({ regionId, input: { personId: target.id } });
      }
      toast.success(`${target.displayName} zugeordnet`);
      setPicking(false);
      setPerson(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Zuordnung nicht gespeichert');
    }
  };

  const skip = async (regionId: string) => {
    try {
      await deleteRegion.mutateAsync({ regionId, notAFace: true });
      toast.success('Gemerkt: hier ist kein Gesicht.');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Nicht gespeichert');
    }
  };

  // ── Von Hand markieren ───────────────────────────────────────────────────

  if (step === 'marking') {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-display text-[19px] font-medium text-panel-ink">
            Wer ist auf dem Bild?
          </h2>
          <Mono className="text-panel-ink-2">
            {named} / {regions.length}
          </Mono>
        </div>

        <p className="text-[12.5px] leading-relaxed text-panel-ink-2">
          Rahmen ziehen oder verschieben — dann Namen zuordnen. Ohne Person bleibt
          es eine offene Frage.
        </p>

        {/* Derselbe Editor wie auf der Bildseite. Er arbeitet auf dem lokalen
            Bild: die Ableitungen vom Server entstehen noch im Hintergrund. */}
        <div className="overflow-hidden rounded-xl bg-panel-sunken p-2">
          <RegionEditor
            src={image.src}
            alt={media.title ?? media.originalFilename}
            regions={regions}
            editable
            /* Dieser Schritt existiert nur zum Markieren – der
               Bearbeitungsmodus ist hier der Normalzustand und muss nicht
               erst eingeschaltet werden. Auf der Bildseite ist es umgekehrt. */
            startEditing
            takenAt={detail.data?.takenAt ?? media.takenAt}
            nonFaces={detail.data?.nonFaces ?? []}
            onCreate={async (box, target, label) => {
              await createRegion.mutateAsync({
                mediaId: media.id,
                // Die Alterseinschätzung kommt später, am Bild – beim Ziehen
                // des Rahmens weiß niemand schon, wen er da einrahmt.
                input: { ...box, personId: target?.id ?? null, label, apparentAge: null },
              });
              toast.success(
                target ? `${target.displayName} markiert` : 'Markierung angelegt – Name offen',
              );
            }}
            onMove={async (regionId, box) => {
              await updateRegion.mutateAsync({ regionId, input: box });
            }}
            onAssign={async (regionId, target, label) => {
              await updateRegion.mutateAsync({
                regionId,
                input: { personId: target?.id ?? null, label },
              });
              toast.success(target ? `${target.displayName} zugeordnet` : 'Markierung aktualisiert');
            }}
            onNotAFace={(regionId) => skip(regionId)}
            onDelete={async (regionId) => {
              await deleteRegion.mutateAsync({ regionId });
              toast.success('Markierung gelöscht');
            }}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn(GOLD_ACTION, 'flex-1')}
            onClick={() => setStep('naming')}
          >
            Fertig markiert
          </Button>
          <Button variant="ghost" size="sm" className={GHOST_ON_INK} onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    );
  }

  // ── Benennen ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2.5 rounded-xl bg-ok-soft px-3.5 py-3 text-[13.5px] font-semibold text-ok">
        <Check className="size-4 shrink-0" aria-hidden />
        Im Archiv angekommen.
        {position ? (
          <Mono className="ml-auto text-ok">
            Bild {position.index + 1} von {position.total}
          </Mono>
        ) : null}
      </div>

      {/* Das Bild als Abzug, mit den gefundenen Rahmen darauf. Gold heißt
          benannt, dunkel gestrichelt heißt offen – dieselbe Sprache wie auf
          der Bildseite. */}
      <PhotoFrame size="lg" tilt className="mx-auto w-full max-w-72">
        <div className="relative">
          <img src={image.src} alt="" className="block w-full" />
          {regions.map((region) => (
            <span
              key={region.id}
              className={cn(
                'pointer-events-none absolute rounded-[2px] border-2',
                region.person
                  ? 'border-accent'
                  : region.id === current?.id
                    ? 'border-panel bg-panel/15 ring-2 ring-accent/50'
                    : 'border-dashed border-panel bg-panel/10',
              )}
              style={{
                left: `${region.x * 100}%`,
                top: `${region.y * 100}%`,
                width: `${region.width * 100}%`,
                height: `${region.height * 100}%`,
              }}
            />
          ))}
        </div>
        <p className="mt-2 px-0.5 font-display text-[12.5px] text-ink-3">
          {media.title?.trim() || 'Ohne Titel'} ·{' '}
          {media.takenAt ? formatFuzzyDate(media.takenAt) : 'heute aufgenommen'}
        </p>
      </PhotoFrame>

      {/* Kopfzeile der Arbeit: was gefunden wurde und wo man steht. */}
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[19px] font-medium text-panel-ink">
          {detecting
            ? 'Gesichter werden gesucht …'
            : regions.length === 0
              ? 'Kein Gesicht gefunden'
              : `${regions.length} ${regions.length === 1 ? 'Gesicht' : 'Gesichter'} gefunden`}
        </h2>
        {regions.length > 0 ? (
          <Mono className="text-panel-ink-2">
            {Math.min(named + 1, regions.length)} von {regions.length}
          </Mono>
        ) : null}
      </div>

      {detecting ? (
        <p className="flex items-center gap-2.5 rounded-xl bg-accent/15 px-3.5 py-3 text-[13px] text-panel-ink">
          <Spinner className="size-4 shrink-0 text-accent" />
          Das rechnet im Browser – das Bild bleibt auf dem Gerät.
        </p>
      ) : null}

      {/* Der aktuelle offene Rahmen. */}
      {!detecting && current ? (
        <div className="flex flex-col gap-3 rounded-[18px] bg-panel-sunken p-3.5">
          <div className="flex items-center gap-3">
            <PhotoFrame size="xs" className="w-16 shrink-0">
              <RegionCrop
                src={image.src}
                region={current}
                imageWidth={image.naturalWidth}
                imageHeight={image.naturalHeight}
                className="aspect-square w-full"
              />
            </PhotoFrame>

            <div className="min-w-0 flex-1">
              {suggestion ? (
                <>
                  <p className="flex items-center gap-1.5 text-[11.5px] text-panel-ink-2">
                    <Sparkles className="size-3 shrink-0 text-accent" aria-hidden />
                    Vorschlag · Ähnlichkeit {Math.round(suggestion.similarity * 100)} %
                  </p>
                  <p className="mt-0.5 truncate text-[16px] font-semibold text-panel-ink">
                    {suggestion.person.displayName}
                  </p>
                  <Mono className="block text-panel-ink-2">
                    {formatLifespanYears(
                      suggestion.person.birthYear,
                      suggestion.person.deathYear,
                    )}
                  </Mono>
                </>
              ) : (
                <>
                  <p className="text-[11.5px] text-panel-ink-2">Markierung zuordnen</p>
                  <p className="mt-0.5 text-[14px] text-panel-ink">
                    {current.label ?? 'Wer ist das?'}
                  </p>
                </>
              )}
            </div>
          </div>

          {/*
            Was für oder gegen den Vorschlag spricht. Angezeigt, nicht nur
            verrechnet: „verwandt mit einer Person auf diesem Bild" ist ein
            Argument, eine verschobene Zahl nicht.

            Als Liste und nicht als Pillen. Pillen waren hier zwei Sorten
            Kapseln in zwei Farben, nebeneinander umbrechend – „wäre damals 96
            gewesen" und „anderer Familienstrang als die Benannten auf dem
            Bild" haben nichts, was sie gemeinsam ausrichtet, und ein
            zweizeilig umgebrochener Satz in einer Kapsel liest sich als
            Beschriftung, nicht als Argument. Untereinander stehen sie an einer
            Kante, die Zeichen bilden eine Spalte, und man sieht mit einem
            Blick, wie viel dafür und wie viel dagegen spricht.

            Dafür zuerst, dagegen darunter: sortiert nach Vorzeichen springt
            das Auge nicht zwischen Grün und Orange hin und her, und was
            gegen den Vorschlag spricht, steht direkt über dem Knopf, mit dem
            man ihn bestätigt.
          */}
          {suggestion && suggestion.reasons.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {[...suggestion.reasons]
                .sort((a, b) => Number(b.favourable) - Number(a.favourable))
                .map((reason) => (
                  <li key={reason.label} className="flex items-start gap-1.5 text-[11.5px]">
                    {/* Das Zeichen trägt das Vorzeichen, nicht eine Fläche:
                        auf der dunklen Tafel wäre eine getönte Kapsel je
                        Argument ein Streifen Farbe zu viel. */}
                    {reason.favourable ? (
                      <Check className="mt-[3px] size-3 shrink-0 text-ok" aria-hidden />
                    ) : (
                      <Minus className="mt-[3px] size-3 shrink-0 text-warn" aria-hidden />
                    )}
                    <span
                      className={cn(
                        'leading-[1.35]',
                        reason.favourable ? 'text-panel-ink-2' : 'text-warn',
                      )}
                    >
                      {reason.label}
                    </span>
                  </li>
                ))}
            </ul>
          ) : null}

          {suggestion ? (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                className={cn(GOLD_ACTION, 'flex-1')}
                loading={verdict.isPending}
                onClick={() => void assign(current.id, suggestion.person)}
              >
                <Check aria-hidden />
                Stimmt
              </Button>
              <Button
                variant="ghost"
                className={cn(TOOL_ON_INK, 'shrink-0')}
                onClick={() => setPicking(true)}
              >
                Andere
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <PersonPicker
                value={person}
                onChange={setPerson}
                placeholder="Person suchen …"
                aria-label="Person für diese Markierung"
              />
              <p className="text-[11.5px] leading-relaxed text-panel-ink-2">
                Ohne Person bleibt die Markierung als offene Frage stehen – die
                Familie kann sie später beantworten.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  className={cn(GOLD_ACTION, 'flex-1')}
                  disabled={!person}
                  loading={updateRegion.isPending}
                  onClick={() => person && void assign(current.id, person)}
                >
                  Speichern
                </Button>
                <Button
                  variant="ghost"
                  className={cn(TOOL_ON_INK, 'shrink-0')}
                  title="Die Erkennung setzt an dieser Stelle künftig keinen Rahmen mehr."
                  onClick={() => void skip(current.id)}
                >
                  <UserRoundX aria-hidden />
                  Kein Gesicht
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Alles benannt – oder es gab nichts zu benennen. */}
      {!detecting && !current ? (
        <p className="rounded-xl bg-panel-sunken px-3.5 py-3 text-[13px] leading-relaxed text-panel-ink-2">
          {regions.length === 0
            ? 'Sehr kleine, abgewandte oder verdeckte Gesichter findet die Erkennung nicht – die setzt man von Hand.'
            : 'Alle gefundenen Gesichter haben einen Namen. Fehlt noch jemand, markiere ihn von Hand.'}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          className={cn(TOOL_ON_INK, 'flex-1')}
          onClick={() => setStep('marking')}
        >
          <PencilLine aria-hidden />
          Von Hand markieren
        </Button>
        <Button asChild variant="ghost" size="sm" className={GHOST_ON_INK}>
          <Link to={`/medien/${media.id}`} onClick={onClose}>
            <ScanFace aria-hidden />
            Bildseite öffnen
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-panel-line pt-3">
        <Button variant="ghost" size="sm" className={cn(GHOST_ON_INK, 'flex-1')} onClick={onClose}>
          Später benennen
        </Button>
        {onNext ? (
          <Button variant="ghost" size="sm" className={cn(GOLD_ACTION, 'flex-1')} onClick={onNext}>
            <Camera aria-hidden />
            {nextLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
